import {
  Box,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { FREE_CREDIT_BYTES } from '~/common/constants/temporary-contsants'
import { useAuthContext } from '~/common/context/auth/auth-context'
import { useFeatures } from '~/common/use-features'
import { useUserByteBreakdown } from '~/common/use-user-byte-breakdown'

const SUBJECTS = [
  { key: 'crops',         label: 'Crops & Schedules' },
  { key: 'tasks',         label: 'Tasks' },
  { key: 'reports',       label: 'Reports' },
  { key: 'organizations', label: 'Farms & Plots' },
  { key: 'profile',       label: 'Profile' },
]

const TotalRow = ({ total }) => {
  const pct = Math.min(100, Math.round((total / FREE_CREDIT_BYTES) * 100))
  const color = pct >= 95 ? 'error' : pct >= 75 ? 'warning' : 'primary'
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>Total Used</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
          {pct} %
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ height: 6, borderRadius: 2 }}
      />
    </Box>
  )
}

const SubjectRow = ({ label, bytes, total }) => {
  const pct = total > 0 ? Math.round((bytes / total) * 100) : 0
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
          {pct} %
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 4, borderRadius: 2 }}
      />
    </Box>
  )
}

export const AccountStorageCard = () => {
  const { user: authUser } = useAuthContext()
  const { features } = useFeatures()
  const { breakdown, total, loading } = useUserByteBreakdown({ userId: authUser?.id })

  if (!features['storage_usage']) return null

  return (
    <Card variant="outlined">
      <CardHeader title="Storage" sx={{ pb: 0 }} />
      <CardContent>
        {loading ? (
          <Stack spacing={2}>
            <Box>
              <Skeleton variant="text" width="40%" sx={{ mb: 0.5 }} />
              <Skeleton variant="rounded" height={6} />
            </Box>
            <Skeleton variant="text" width="80%" />
            {SUBJECTS.map(({ key }) => (
              <Box key={key}>
                <Skeleton variant="text" width="40%" sx={{ mb: 0.5 }} />
                <Skeleton variant="rounded" height={4} />
              </Box>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <TotalRow total={total ?? 0} />
            <Typography variant="body2" color="text.secondary">
              The breakdown below shows how your used storage is distributed across categories.
            </Typography>
            {SUBJECTS.map(({ key, label }) => (
              <SubjectRow
                key={key}
                label={label}
                bytes={breakdown?.[key] ?? 0}
                total={total ?? 0}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}
